import { C } from "../components/reporting/reportingStyles";

// Turns an OrderStatusHistory row into something a human can read.
//
// New rows carry `metadata.actor` (written by the API's orderStatusActor util). Older rows only have
// `updatedBy`, which was free text — an Odoo uid from POS, "system" from a cron, "KDS_WEB", or "".
// Both are resolved here so history written before the change still renders sensibly.

export const STATUS_SOURCES = {
  system: "system",
  pos: "pos",
  kdsWeb: "kds_web",
  kdsApp: "kds_app",
  customer: "customer",
  admin: "admin",
  partner: "partner",
};

const LEGACY_UPDATED_BY = {
  system: STATUS_SOURCES.system,
  system_refund: STATUS_SOURCES.customer,
  customer: STATUS_SOURCES.customer,
  KDS_WEB: STATUS_SOURCES.kdsWeb,
  KDS_APP: STATUS_SOURCES.kdsApp,
  POS: STATUS_SOURCES.pos,
  admin: STATUS_SOURCES.admin,
};

const SOURCE_META = {
  [STATUS_SOURCES.pos]: { label: "Branch (POS)", tone: { bg: C.blueLight, fg: C.blue } },
  [STATUS_SOURCES.system]: { label: "System", tone: { bg: C.borderLight, fg: C.textMed } },
  [STATUS_SOURCES.kdsWeb]: { label: "Web KDS", tone: { bg: C.coralLight, fg: C.coral } },
  [STATUS_SOURCES.kdsApp]: { label: "KDS App", tone: { bg: C.coralLight, fg: C.coral } },
  [STATUS_SOURCES.customer]: { label: "Customer", tone: { bg: C.orangeLight, fg: C.orange } },
  [STATUS_SOURCES.admin]: { label: "Admin Panel", tone: { bg: C.purpleLight, fg: C.purple } },
  [STATUS_SOURCES.partner]: { label: "Ordering App", tone: { bg: C.greenLight, fg: C.green } },
};

const UNKNOWN_META = { label: "Unknown", tone: { bg: C.borderLight, fg: C.textLight } };

const resolveSource = (history) => {
  const fromActor = history?.metadata?.actor?.source;
  if (fromActor && SOURCE_META[fromActor]) return fromActor;

  const updatedBy = history?.updatedBy;
  if (updatedBy && LEGACY_UPDATED_BY[updatedBy]) return LEGACY_UPDATED_BY[updatedBy];
  // POS has always sent the raw Odoo uid as updatedBy.
  if (updatedBy && /^\d+$/.test(String(updatedBy).trim())) return STATUS_SOURCES.pos;

  return null;
};

// When nobody recorded a reason, say what we can infer rather than leaving a bare "--".
const fallbackReason = (source, newStatus) => {
  const cancelled = newStatus === "canceled";
  if (source === STATUS_SOURCES.system) {
    return cancelled ? "Canceled automatically by the system." : "Automatic update by the system.";
  }
  if (source === STATUS_SOURCES.pos) {
    return cancelled ? "No reason was entered on the POS tablet." : "Updated on the POS tablet.";
  }
  if (source === STATUS_SOURCES.kdsWeb || source === STATUS_SOURCES.kdsApp) {
    return cancelled ? "Canceled from the kitchen display." : "Updated from the kitchen display.";
  }
  if (source === STATUS_SOURCES.customer) {
    return cancelled ? "Canceled by the customer from the app." : "Updated by the customer.";
  }
  if (source === STATUS_SOURCES.admin) {
    return cancelled ? "No reason was entered in the admin panel." : "Updated from the admin panel.";
  }
  if (source === STATUS_SOURCES.partner) {
    return cancelled ? "Cancellation requested by the ordering app." : "Updated by the ordering app.";
  }
  // Pre-existing rows written before the origin was recorded.
  return cancelled ? "No reason was recorded for this cancellation." : null;
};

/**
 * @returns {{
 *   source: string|null, sourceLabel: string, tone: {bg: string, fg: string},
 *   actorName: string|null, actorDetails: string[], reason: string|null,
 * }}
 */
export const describeStatusChange = (history) => {
  const source = resolveSource(history);
  const meta = SOURCE_META[source] || UNKNOWN_META;
  const actor = history?.metadata?.actor || {};

  // Whoever was logged in on the tablet / screen when the change was made.
  const actorName = actor.name || null;

  const details = [];
  if (actor.username && actor.username !== actorName) details.push(actor.username);
  // Only a numeric id is a real POS user reference — the other legacy `updatedBy` values
  // ("system", "system_refund", "KDS_WEB") are sentinels already covered by the badge.
  const numericId = [actor.id, history?.updatedBy].find((v) => v != null && /^\d+$/.test(String(v).trim()));
  if (!actorName && numericId) details.push(`POS user #${numericId}`);
  // Some actor names already carry the branch ("Web KDS — Wags Al Olaya"); don't repeat it.
  if (actor.branchName && !(actorName || "").includes(actor.branchName)) details.push(actor.branchName);

  const deviceId = actor.deviceId || history?.metadata?.deviceID;
  if (deviceId && deviceId !== "system") details.push(`Device ${deviceId}`);

  const reason = (history?.reason || "").trim();

  return {
    source,
    sourceLabel: meta.label,
    tone: meta.tone,
    actorName,
    actorDetails: details,
    reason: reason || fallbackReason(source, history?.newStatus),
  };
};

/**
 * The row that actually canceled the order — used for the banner on a canceled order.
 * History arrives newest-first from the API, but sort defensively.
 */
export const findCancellationEntry = (histories) => {
  if (!Array.isArray(histories) || !histories.length) return null;
  return [...histories]
    .filter((h) => h?.newStatus === "canceled")
    .sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))[0] || null;
};
